import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  HomeIcon,
  FolderIcon,
  ChartBarIcon,
  UsersIcon,
  CogIcon,
  PlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  SparklesIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  Square3Stack3DIcon,
  CheckCircleIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeIconSolid,
  FolderIcon as FolderIconSolid,
  ChartBarIcon as ChartBarIconSolid,
  UsersIcon as UsersIconSolid,
  CogIcon as CogIconSolid,
  SparklesIcon as SparklesIconSolid,
  ChatBubbleLeftRightIcon as ChatBubbleLeftRightIconSolid,
  DocumentTextIcon as DocumentTextIconSolid,
  Square3Stack3DIcon as Square3Stack3DIconSolid,
  QuestionMarkCircleIcon as QuestionMarkCircleIconSolid,
  CheckCircleIcon as CheckCircleIconSolid,
} from '@heroicons/react/24/solid';

const navigation = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: HomeIcon,
    iconSolid: HomeIconSolid,
    description: 'Overview and insights',
  },
  {
    name: 'Projects',
    href: '/projects',
    icon: FolderIcon,
    iconSolid: FolderIconSolid,
    description: 'Manage your projects',
  },
  {
    name: 'Analytics',
    href: '/analytics',
    icon: ChartBarIcon,
    iconSolid: ChartBarIconSolid,
    description: 'Project analytics',
  },
  {
    name: 'AI Insights',
    href: '/ai-insights',
    icon: SparklesIcon,
    iconSolid: SparklesIconSolid,
    description: 'AI learning dashboard',
  },
  {
    name: 'Team',
    href: '/team',
    icon: UsersIcon,
    iconSolid: UsersIconSolid,
    description: 'Team management',
  },
  {
    name: 'AI Chat',
    href: '/chat',
    icon: ChatBubbleLeftRightIcon,
    iconSolid: ChatBubbleLeftRightIconSolid,
    description: 'AI assistant',
  },
  {
    name: 'Documents',
    href: '/documents',
    icon: DocumentTextIcon,
    iconSolid: DocumentTextIconSolid,
    description: 'File management',
  },
  {
    name: 'Integrations',
    href: '/integrations',
    icon: Square3Stack3DIcon,
    iconSolid: Square3Stack3DIconSolid,
    description: 'Connect services',
  },
  {
    name: 'Approvals',
    href: '/approvals',
    icon: CheckCircleIcon,
    iconSolid: CheckCircleIconSolid,
    description: 'HITL checkpoints',
  },
  {
    name: 'Help & FAQ',
    href: '/help',
    icon: QuestionMarkCircleIcon,
    iconSolid: QuestionMarkCircleIconSolid,
    description: 'Help and support',
  },
  {
    name: 'Settings',
    href: '/settings',
    icon: CogIcon,
    iconSolid: CogIconSolid,
    description: 'System configuration',
  },
];

export default function Sidebar() {
  const { sidebarCollapsed, toggleSidebar } = useTheme();
  const { user } = useAuth();
  const location = useLocation();

  const isActive = (href) => {
    if (href === '/dashboard') {
      return location.pathname === '/' || location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div
      className={`fixed inset-y-0 left-0 z-50 bg-white border-r border-gray-200 transition-all duration-300 ${
        sidebarCollapsed ? 'w-16' : 'w-64'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
        {!sidebarCollapsed && (
          <div className="flex items-center space-x-2">
            <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg">
              <SparklesIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">AI PM</h1>
              <p className="text-xs text-gray-500">Project Manager</p>
            </div>
          </div>
        )}
        
        <button
          onClick={toggleSidebar}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? (
            <ChevronRightIcon className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronLeftIcon className="w-5 h-5 text-gray-500" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1">
        {/* Quick Action - New Project */}
        <NavLink
          to="/projects/new"
          className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
            location.pathname === '/projects/new'
              ? 'bg-primary-100 text-primary-700 border border-primary-200'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
          }`}
          title={sidebarCollapsed ? 'New Project' : ''}
        >
          <PlusIcon className="w-5 h-5 flex-shrink-0" />
          {!sidebarCollapsed && (
            <span className="ml-3">New Project</span>
          )}
        </NavLink>

        {/* Divider */}
        <div className="border-t border-gray-200 my-4" />

        {/* Main Navigation */}
        {navigation.map((item) => {
          const active = isActive(item.href);
          const Icon = active ? item.iconSolid : item.icon;
          
          return (
            <NavLink
              key={item.name}
              to={item.href}
              className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                active
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
              title={sidebarCollapsed ? item.name : ''}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {!sidebarCollapsed && (
                <div className="ml-3">
                  <div className="text-sm font-medium">{item.name}</div>
                  <div className="text-xs text-gray-500">{item.description}</div>
                </div>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* User Profile */}
      {!sidebarCollapsed && (
        <div className="border-t border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-white">
                  {user?.name?.charAt(0) || 'U'}
                </span>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.name || 'User'}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user?.role || 'Member'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
